import { useMemo, useState, type CSSProperties } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useCari } from '../../context/CariContext'
import { saveDebtor } from '../../services/cari'
import { formatTRY } from '../../utils/money'
import { BalanceAfter } from './BalanceAfter'
import { PlusIcon, SearchIcon, UserIcon } from '../icons'
import type { DebtorBalance } from '../../types'

interface Props {
  totalKurus: number
  onPick: (debtor: DebtorBalance, note: string) => void
  onCancel: () => void
}

/** Case- and alphabet-insensitive, so "çelik" finds "Çelik". */
const fold = (s: string) => s.toLocaleLowerCase('tr-TR')

/**
 * Choosing whose account a sale goes on.
 *
 * Sorted by who was here most recently rather than alphabetically: the same
 * few people come back through a day, and a list that puts them at the top is
 * one tap where an alphabetical one is a search every time.
 *
 * Picking a person does not write the debt yet: a confirm step shows who and
 * how much, with room for a note ("Masa 5, iki kişilik"). A tap on a name
 * used to go straight onto the account, before anyone could say what it was
 * for, and a mis-tap in a list of names put the bill on the wrong person.
 *
 * Creating a person is part of this screen, not a trip to a settings page.
 * The moment someone asks to put lunch on their account is the moment their
 * account has to exist, and there is a queue behind them.
 */
/** Kişinin simge rengi: adından türüyor, aynı kişi hep aynı renkte (25 Eki 2026). */
const AVATAR_COLORS = ['#f15a24', '#2a78d6', '#16a34a', '#7c5cd6', '#db2777', '#0891b2', '#ca8a04', '#4f46e5']
function avatarIndex(name: string): number {
  let h = 0
  for (const ch of name.toLocaleLowerCase('tr')) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  return h % AVATAR_COLORS.length
}

/** Listedeki renkler: alt alta iki kişi aynı renge düşerse ikincisi kayıyor. */
function avatarColors(names: string[]): string[] {
  let prev = -1
  return names.map((n) => {
    let i = avatarIndex(n)
    if (i === prev) i = (i + 1) % AVATAR_COLORS.length
    prev = i
    return AVATAR_COLORS[i]
  })
}

export function DebtorPicker({ totalKurus, onPick, onCancel }: Props) {
  const { tenantId } = useAuth()
  const { debtors } = useCari()

  const [query, setQuery] = useState('')
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newPhone, setNewPhone] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** The person chosen, waiting on the confirm step. */
  const [picked, setPicked] = useState<DebtorBalance | null>(null)
  const [note, setNote] = useState('')

  const shown = useMemo(() => {
    const q = fold(query.trim())
    return debtors
      .filter((d) => d.active && (!q || fold(d.name).includes(q)))
      .sort((a, b) => {
        // Anyone with a balance outranks anyone without: they are the ones
        // the till is likely to be asked about.
        if ((a.balanceKurus > 0) !== (b.balanceKurus > 0)) {
          return a.balanceKurus > 0 ? -1 : 1
        }
        if (a.lastMovedAt !== b.lastMovedAt) return b.lastMovedAt - a.lastMovedAt
        return a.name.localeCompare(b.name, 'tr')
      })
  }, [debtors, query])
  const colors = useMemo(() => avatarColors(shown.map((d) => d.name)), [shown])

  async function onCreate() {
    if (!tenantId || !newName.trim()) return
    setBusy(true)
    setError(null)
    try {
      const id = await saveDebtor(tenantId, {
        name: newName.trim(),
        phone: newPhone.trim(),
        active: true,
      })
      // On to the confirm step, same as picking someone from the list. The
      // person was created because they are standing at the counter now.
      setCreating(false)
      setBusy(false)
      setPicked({
        id,
        name: newName.trim(),
        phone: newPhone.trim() || undefined,
        active: true,
        balanceKurus: 0,
        lastMovedAt: 0,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kaydedilemedi.')
      setBusy(false)
    }
  }

  const confirm = () => {
    if (!picked || busy) return
    setBusy(true)
    onPick(picked, note.trim())
  }

  if (picked) {
    return (
      <div className="dlg" role="dialog" aria-modal="true">
        <div className="dlg__card dlg__card--ask">
          <h2>Borca Yaz</h2>

          {/* Tutar kutusu masa seçimindekiyle aynı (25 Eki 2026). */}
          <div className="tablepick__sum">
            <span className="tablepick__sum-text">
              <span>Borca yazılacak</span>
              <small>{picked.name}</small>
            </span>
            <strong>{formatTRY(totalKurus)}</strong>
          </div>

          {/* Alacaklı (avanslı) kişide satış alacaktan düşer (2 Kas 2026). */}
          <BalanceAfter before={picked.balanceKurus} after={picked.balanceKurus + totalKurus} />

          <label className="dlgfield">
            <span>Açıklama (isteğe bağlı)</span>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Örn. Ahmet Cihan yazdırdı, Melih Bey'in isteği…"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  confirm()
                }
              }}
            />
          </label>

          <div className="dlg__buttons dlg__buttons--even">
            <button
              type="button"
              className="btn btn--ghost"
              disabled={busy}
              onClick={() => {
                setPicked(null)
                setNote('')
              }}
            >
              Geri
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy}
              onClick={confirm}
            >
              {busy ? 'Yazılıyor…' : 'Borca yaz'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (creating) {
    return (
      <div className="dlg" role="dialog" aria-modal="true">
        <div className="dlg__card dlg__card--ask">
          <h2>Yeni cari hesap</h2>

          <label className="dlgfield">
            <span>İsim</span>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newName.trim()) {
                  e.preventDefault()
                  void onCreate()
                }
              }}
            />
          </label>

          <label className="dlgfield">
            <span>Telefon (isteğe bağlı)</span>
            <input
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              inputMode="tel"
            />
          </label>

          {error && <p className="dlg__warn">{error}</p>}

          <div className="dlg__buttons dlg__buttons--even">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setCreating(false)
                setError(null)
              }}
            >
              Geri
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy || !newName.trim()}
              onClick={() => void onCreate()}
            >
              {busy ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="dlg" role="dialog" aria-modal="true">
      <div className="dlg__card dlg__card--ask">
        <h2>Borca Yaz</h2>

        {/* Tutar kutusu masa seçimindekiyle aynı (25 Eki 2026). */}
        <div className="tablepick__sum">
          <span className="tablepick__sum-text">
            <span>Borca yazılacak</span>
            <small>Kimin hesabına?</small>
          </span>
          <strong>{formatTRY(totalKurus)}</strong>
        </div>

        <div className="debtors__search">
          <SearchIcon />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="İsim ara"
            autoComplete="off"
          />
        </div>

        {shown.length === 0 ? (
          <p className="dlg__empty">
            {query ? 'Bu isimde kayıt yok.' : 'Henüz cari hesap açılmamış.'}
          </p>
        ) : (
          <ul className="debtors__list">
            {shown.map((d, di) => (
              <li key={d.id}>
                <button type="button" className="debtors__row" onClick={() => setPicked(d)}>
                  {/* Kişi simgesi ve ayrı satırlar (25 Eki 2026, kullanıcı). */}
                  <span
                    className="debtors__avatar"
                    aria-hidden="true"
                    style={{ '--av': colors[di] } as CSSProperties}
                  >
                    <UserIcon />
                  </span>
                  <span className="debtors__name">{d.name}</span>
                  {d.balanceKurus !== 0 && (
                    <span
                      className={`debtors__bal ${d.balanceKurus < 0 ? 'is-credit' : ''}`}
                    >
                      {d.balanceKurus > 0
                        ? formatTRY(d.balanceKurus)
                        : `+${formatTRY(-d.balanceKurus)}`}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="dlg__buttons dlg__buttons--even">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Vazgeç
          </button>
          <button
            type="button"
            className="btn btn--outline"
            onClick={() => {
              // Whatever was typed into the search is almost always the name
              // being looked for, carrying it over saves typing it twice.
              setNewName(query.trim())
              setCreating(true)
            }}
          >
            <PlusIcon />
            Yeni kişi
          </button>
        </div>
      </div>
    </div>
  )
}
